import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useFeatureFlags } from '@/contexts/FeatureFlagContext';
import { TUTORIALS, Tutorial, TourRole } from '@/config/tours';
import {
  getUserKey,
  loadTutorialState,
  markAllCompleted,
  markCompleted,
  markWelcomeSeen,
  resetTutorial,
} from '@/utils/tutorialsStorage';
import TutorialsMenu from '@/components/shared/TutorialsMenu';
import TourRunner from '@/components/shared/TourRunner';
import TourWelcome from '@/components/shared/TourWelcome';

interface TutorialsContextType {
  /** Open the Tutorials launcher menu. */
  openTutorials: () => void;
  /** Start a tutorial by id (navigates to its launch route if needed). */
  startTutorial: (id: string) => void;
  /** Tutorials the current user can see (role + feature-flag filtered). */
  availableTutorials: Tutorial[];
  /** Subset of `availableTutorials` whose launchRoute matches this screen. */
  currentScreenTutorials: Tutorial[];
  /** The tour currently running, if any. */
  activeTutorial: Tutorial | null;
  /** Ids the user has completed (or skipped). */
  completedIds: Set<string>;
}

const TutorialsContext = createContext<TutorialsContextType | null>(null);

/** Access the Tutorials context. */
export const useTutorials = (): TutorialsContextType => {
  const ctx = useContext(TutorialsContext);
  if (!ctx) throw new Error('useTutorials must be used within a TutorialsProvider');
  return ctx;
};

function readRole(): TourRole | undefined {
  try {
    const raw = localStorage.getItem('user_info');
    if (!raw) return undefined;
    const role = JSON.parse(raw)?.role;
    return role === 'athlete' || role === 'coach' || role === 'admin' ? role : undefined;
  } catch {
    return undefined;
  }
}

/** Does the current pathname belong to a tutorial's launch screen? */
function matchesScreen(pathname: string, tutorial: Tutorial): boolean {
  const routes = tutorial.matchRoutes ?? [tutorial.launchRoute];
  return routes.some((r) => pathname === r || pathname.startsWith(r + '/'));
}

/**
 * Provides the guided-tutorials experience: the sidebar-launched menu, the
 * running spotlight tour, per-user completion state, and auto-start of every
 * not-yet-completed tutorial for the screen the user is on (one at a time, in
 * catalog order — finishing one surfaces the next due on that screen; Skip /
 * Esc opts out of all remaining auto-start tours). A tour abandoned mid-way
 * (navigation away, reload) is NOT marked completed, so it auto-offers again
 * the next time the user reaches its screen. Gated by role, feature flags, an
 * auth token, and flags-loaded, and scoped to each tutorial's launch route —
 * so nothing fires on public/unauthenticated screens like the landing page.
 */
export const TutorialsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { flags, loading } = useFeatureFlags();

  const [userKey, setUserKey] = useState<string>(() => getUserKey());
  const [role, setRole] = useState<TourRole | undefined>(() => readRole());
  const [progress, setProgress] = useState(() => loadTutorialState(getUserKey()));
  const [menuOpen, setMenuOpen] = useState(false);
  const [active, setActive] = useState<Tutorial | null>(null);
  // First-ever auto-start is held behind a one-time welcome modal so the
  // spotlight overlay doesn't just appear unannounced.
  const [welcomeFor, setWelcomeFor] = useState<Tutorial | null>(null);
  // Tours abandoned mid-run this session. Not persisted (a fresh visit should
  // re-offer them) but excluded from auto-start until then, so an abandon on a
  // still-matching screen can't immediately relaunch the same tour.
  const sessionAbandonedRef = useRef<Set<string>>(new Set());

  // user_info mutates on login/logout/settings changes — re-read on navigation
  // (same approach as SidebarRail).
  useEffect(() => {
    const key = getUserKey();
    setUserKey(key);
    setRole(readRole());
    setProgress(loadTutorialState(key));
  }, [location.pathname]);

  const applicable = useMemo<Tutorial[]>(
    () =>
      TUTORIALS.filter(
        (t) =>
          (!t.roles || (role !== undefined && t.roles.includes(role))) &&
          (!t.featureFlag || Boolean(flags[t.featureFlag])),
      ),
    [role, flags],
  );

  const currentScreenTutorials = useMemo(
    () => applicable.filter((t) => matchesScreen(location.pathname, t)),
    [applicable, location.pathname],
  );

  const completedIds = useMemo(() => new Set(progress.completed), [progress]);

  // Auto-start every not-yet-completed tutorial for the current screen, one
  // after another: wait for flags, require an auth token, require being on
  // the tour's launch route, and pick the first tutorial that hasn't been
  // completed (finished OR skip-all) or abandoned this session. Re-runs
  // whenever `active`/`progress`/route changes, so finishing one tutorial
  // immediately surfaces the next one for this screen, and reaching a new
  // screen auto-activates whatever is due there. Skip-all marks every
  // applicable auto-start tutorial completed, so nothing further fires.
  useEffect(() => {
    if (loading || active || menuOpen || welcomeFor) return;
    const hasToken = typeof window !== 'undefined' && !!localStorage.getItem('access');
    if (!hasToken) return;

    const candidate = applicable.find(
      (t) =>
        t.autoStart !== false &&
        matchesScreen(location.pathname, t) &&
        !progress.completed.includes(t.id) &&
        !sessionAbandonedRef.current.has(t.id),
    );
    if (candidate) {
      // The very first tour a user would ever see is introduced by a welcome
      // modal; after that, tours start directly.
      if (!progress.welcomeSeen) setWelcomeFor(candidate);
      else setActive(candidate);
    }
  }, [
    loading, applicable, location.pathname,
    progress, active, menuOpen, welcomeFor, userKey,
  ]);

  const startTutorial = (id: string) => {
    const tutorial = applicable.find((t) => t.id === id) ?? null;
    if (!tutorial) return;
    setMenuOpen(false);
    // A manual start means the user already knows what tutorials are.
    if (!progress.welcomeSeen) {
      markWelcomeSeen(userKey);
      setProgress(loadTutorialState(userKey));
    }
    setWelcomeFor(null);
    // Manual start clears any abandon so a re-abandon still works next visit.
    sessionAbandonedRef.current.delete(id);
    if (!matchesScreen(location.pathname, tutorial) && tutorial.navigable !== false) {
      navigate(tutorial.launchRoute);
    }
    setActive(tutorial);
  };

  /** Natural completion of the current tour only — next due tour may chain. */
  const handleFinish = () => {
    if (active) {
      markCompleted(userKey, active.id);
      setProgress(loadTutorialState(userKey));
    }
    setActive(null);
  };

  /**
   * Skip / Esc: opt out of every applicable auto-start tutorial (not just the
   * current one). Manual Replay from the Tutorials menu still works after.
   */
  const handleSkipAll = () => {
    const ids = applicable
      .filter((t) => t.autoStart !== false)
      .map((t) => t.id);
    // Always include the active tour even if it was menu-only (autoStart: false).
    if (active && !ids.includes(active.id)) ids.push(active.id);
    markAllCompleted(userKey, ids);
    setProgress(loadTutorialState(userKey));
    setActive(null);
  };

  /**
   * The user left the tour's screen mid-run (browser back, deep link). Don't
   * mark completed — the tour re-offers on the next visit to its screen.
   */
  const handleAbandon = () => {
    if (active) sessionAbandonedRef.current.add(active.id);
    setActive(null);
  };

  /** Welcome modal → "Show me around": start the tour that was waiting. */
  const handleWelcomeStart = () => {
    markWelcomeSeen(userKey);
    setProgress(loadTutorialState(userKey));
    setActive(welcomeFor);
    setWelcomeFor(null);
  };

  /** Welcome modal → "Skip the tours": same opt-out as in-tour skip-all. */
  const handleWelcomeSkip = () => {
    markWelcomeSeen(userKey);
    const ids = applicable.filter((t) => t.autoStart !== false).map((t) => t.id);
    markAllCompleted(userKey, ids);
    setProgress(loadTutorialState(userKey));
    setWelcomeFor(null);
  };

  const handleReset = (id: string) => {
    resetTutorial(userKey, id);
    setProgress(loadTutorialState(userKey));
    sessionAbandonedRef.current.delete(id);
  };

  return (
    <TutorialsContext.Provider
      value={{
        openTutorials: () => setMenuOpen(true),
        startTutorial,
        availableTutorials: applicable,
        currentScreenTutorials,
        activeTutorial: active,
        completedIds,
      }}
    >
      {children}
      {menuOpen && !active && (
        <TutorialsMenu
          tutorials={applicable}
          currentScreenIds={new Set(currentScreenTutorials.map((t) => t.id))}
          completedIds={completedIds}
          onStart={startTutorial}
          onReset={handleReset}
          onClose={() => setMenuOpen(false)}
        />
      )}
      {welcomeFor && !active && !menuOpen && (
        <TourWelcome onStart={handleWelcomeStart} onSkipAll={handleWelcomeSkip} />
      )}
      {active && (
        <TourRunner
          key={active.id}
          tutorial={active}
          onFinish={handleFinish}
          onSkipAll={handleSkipAll}
          onAbandon={handleAbandon}
        />
      )}
    </TutorialsContext.Provider>
  );
};
